import { createSlice } from "@reduxjs/toolkit";
import { UserSignIn, LogOut, GetUserRoles, ForgotPassword, VerifyForgotPasswordCode, UpdatePassword, GetResendCode, VerifyCode } from "../Actions/authAction";
import customFailAlert from "../../Alerts/customFailAlert";

const initialState = {
  token: "",
  refreshToken: "",
  status: "",
  message: "",
  loading: false,
  error: "",
  userDetails: {},
  userInfo: {},
  userRoles: [],
  forgotPasswordLoading: false,
  forgotPasswordMessage: "",
  forgotPasswordStatus: "",
  verifyCodeLoading: false,
  verifyCodeMessage: "",
  verifyCodeStatus: "",
  updatePasswordLoading: false,
  updatePasswordMessage: "",
  updatePasswordStatus: "",
  resendCodeLoading: false,
  resendCodeMessage: "",
  resendCodeStatus: "",
  verifyOtpLoading: false,
  verifyOtpMessage: "",
  verifyOtpStatus: "",
};

const authReducer = createSlice({
  name: "user",
  initialState,
  reducers: {
    setAuthDetails: (state, action) => {
      state.token = action.payload.token;
      state.email = action.payload.email;
      state.refreshToken = action.payload.refreshToken;
      localStorage.setItem("token", action.payload.token);
      localStorage.setItem("refreshToken", action.payload.refreshToken);
    },
    addToken: (state) => {
      state.token = localStorage.getItem("token");
      state.refreshToken = localStorage.getItem("refreshToken");
    },
    clearAuthDetails: (state) => {
      state.token = "";
      state.email = "";
      state.refreshToken = "";
      state.userDetails = {};
      state.userInfo = {};
    },
    userLogout: (state) => {
      state.token = "";
      state.refreshToken = "";
      state.email = "";
      state.userDetails = {};
      state.userInfo = {};
      localStorage.clear();
    },
    clearForgotPasswordState: (state) => {
      state.forgotPasswordLoading = false;
      state.forgotPasswordMessage = "";
      state.forgotPasswordStatus = "";
    },
    clearUpdatePasswordState: (state) => {
      state.updatePasswordLoading = false;
      state.updatePasswordMessage = "";
      state.updatePasswordStatus = "";
    },
    clearVerifyCodeState: (state) => {
      state.verifyCodeLoading = false;
      state.verifyCodeMessage = "";
      state.verifyCodeStatus = "";
    },
    clearResendCodeState: (state) => {
      state.resendCodeLoading = false;
      state.resendCodeMessage = "";
      state.resendCodeStatus = "";
    },
    clearVerifyOtpState: (state) => {
      state.verifyOtpLoading = false;
      state.verifyOtpMessage = "";
      state.verifyOtpStatus = "";
    },
  },
  extraReducers: {
    [UserSignIn.pending]: (state) => {
      state.loading = true;
    },
    [UserSignIn.fulfilled]: (
      state,
      {
        payload: {
          responseData: { data, message, statusCode },
          requestBody: { portal },
        },
      }
    ) => {
      state.loading = false;
      if (statusCode === 201 && data) {
        const { accessToken, refreshToken } = data;
        state.token = accessToken;
        state.refreshToken = refreshToken;
        localStorage.setItem("token", accessToken);
        localStorage.setItem("refreshToken", refreshToken);
        const userInfo = { ...data, portal };
        state.userInfo = userInfo;
        state.message = message;
        state.status = "SUCCESS";
      } else {
        customFailAlert(message);
        state.error = message;
        state.status = "FAILED";
      }
    },
    [UserSignIn.rejected]: (state) => {
      state.loading = false;
      customFailAlert("User Not Authorized.");
    },
    [GetUserRoles.pending]: (state, action) => {
      state.loading = true;
    },
    [GetUserRoles.fulfilled]: (
      state,
      { payload: { status, data, message, statusCode } }
    ) => {
      state.loading = false;

      if (statusCode === 200) {
        state.message = message;
        state.status = status;
        state.code = statusCode;
        state.userRoles = data;
        // Don't overwrite userInfo - it's already set by UserContext during login
        // Only update if userInfo doesn't exist yet in localStorage
        const existingUserInfo = localStorage.getItem("userInfo");
        if (!existingUserInfo || existingUserInfo === "undefined") {
          const portal = data[0]?.role;
          const userPortal = { portal: portal };
          localStorage.setItem("userInfo", JSON.stringify(userPortal));
        }
      } else {
        state.error = message;
        state.status = status;
        state.code = statusCode;
      }
    },
    [GetUserRoles.rejected]: (state, action) => {
      state.loading = false;
    },
    [LogOut.fulfilled]: (state) => {
      state.token = "";
      state.refreshToken = "";
      state.user = null;
      state.loading = false;
    },
    [ForgotPassword.pending]: (state) => {
      state.forgotPasswordLoading = true;
      state.forgotPasswordMessage = "";
      state.forgotPasswordStatus = "";
    },
    [ForgotPassword.fulfilled]: (
      state,
      { payload: { status, data, message, statusCode } }
    ) => {
      state.forgotPasswordLoading = false;
      if (statusCode === 200 || statusCode === 201) {
        state.forgotPasswordMessage = message;
        state.forgotPasswordStatus = "SUCCESS";
      } else {
        customFailAlert(message);
        state.forgotPasswordMessage = message;
        state.forgotPasswordStatus = "FAILED";
      }
    },
    [ForgotPassword.rejected]: (state, action) => {
      state.forgotPasswordLoading = false;
      state.forgotPasswordMessage = "Failed to send reset email";
      state.forgotPasswordStatus = "FAILED";
      customFailAlert("Failed to send reset email");
    },
    [VerifyForgotPasswordCode.pending]: (state) => {
      state.verifyCodeLoading = true;
      state.verifyCodeMessage = "";
      state.verifyCodeStatus = "";
    },
    [VerifyForgotPasswordCode.fulfilled]: (
      state,
      { payload: { status, data, message, statusCode } }
    ) => {
      state.verifyCodeLoading = false;
      if (statusCode === 200 || statusCode === 201) {
        state.verifyCodeMessage = message;
        state.verifyCodeStatus = "SUCCESS";
      } else {
        customFailAlert(message);
        state.verifyCodeMessage = message;
        state.verifyCodeStatus = "FAILED";
      }
    },
    [VerifyForgotPasswordCode.rejected]: (state, action) => {
      state.verifyCodeLoading = false;
      state.verifyCodeMessage = "Failed to verify reset code";
      state.verifyCodeStatus = "FAILED";
      customFailAlert("Failed to verify reset code");
    },
    [UpdatePassword.pending]: (state) => {
      state.updatePasswordLoading = true;
      state.updatePasswordMessage = "";
      state.updatePasswordStatus = "";
    },
    [UpdatePassword.fulfilled]: (
      state,
      { payload: { status, data, message, statusCode } }
    ) => {
      state.updatePasswordLoading = false;
      if (statusCode === 200 || statusCode === 201) {
        state.updatePasswordMessage = message;
        state.updatePasswordStatus = "SUCCESS";
      } else {
        customFailAlert(message);
        state.updatePasswordMessage = message;
        state.updatePasswordStatus = "FAILED";
      }
    },
    [UpdatePassword.rejected]: (state, action) => {
      state.updatePasswordLoading = false;
      state.updatePasswordMessage = "Failed to update password";
      state.updatePasswordStatus = "FAILED";
      customFailAlert("Failed to update password");
    },
    [GetResendCode.pending]: (state) => {
      state.resendCodeLoading = true;
      state.resendCodeMessage = "";
      state.resendCodeStatus = "";
    },
    [GetResendCode.fulfilled]: (
      state,
      { payload: { status, data, message, statusCode } }
    ) => {
      state.resendCodeLoading = false;
      if (statusCode === 200 || statusCode === 201) {
        state.resendCodeMessage = message;
        state.resendCodeStatus = "SUCCESS";
      } else {
        customFailAlert(message);
        state.resendCodeMessage = message;
        state.resendCodeStatus = "FAILED";
      }
    },
    [GetResendCode.rejected]: (state, action) => {
      state.resendCodeLoading = false;
      state.resendCodeMessage = "Failed to resend code";
      state.resendCodeStatus = "FAILED";
      customFailAlert("Failed to resend code");
    },
    [VerifyCode.pending]: (state) => {
      state.verifyOtpLoading = true;
      state.verifyOtpMessage = "";
      state.verifyOtpStatus = "";
    },
    [VerifyCode.fulfilled]: (
      state,
      { payload: { status, data, message, statusCode } }
    ) => {
      state.verifyOtpLoading = false;
      if (statusCode === 200 || statusCode === 201) {
        state.verifyOtpMessage = message;
        state.verifyOtpStatus = "SUCCESS";
      } else {
        customFailAlert(message);
        state.verifyOtpMessage = message;
        state.verifyOtpStatus = "FAILED";
      }
    },
    [VerifyCode.rejected]: (state, action) => {
      state.verifyOtpLoading = false;
      state.verifyOtpMessage = "Failed to verify OTP code";
      state.verifyOtpStatus = "FAILED";
      customFailAlert("Failed to verify OTP code");
    },
  },
});

export const {
  addToken,
  logout,
  setAuthDetails,
  clearAuthDetails,
  userLogout,
  clearForgotPasswordState,
  clearUpdatePasswordState,
  clearVerifyCodeState,
  clearResendCodeState,
  clearVerifyOtpState,
} = authReducer.actions;
export default authReducer.reducer;
